// v52 — Profile menu. Rewritten for the v2 (tier) architecture.
//
// WHAT WAS DUPLICATED, AND WHY IT MATTERED
//
// The trigger used to print `user.role` — the v1 axis, where one word meant
// identity, tenant, tier and authority all at once. With OPERATING AS now
// sitting directly above it stating the tier, the tenant and the authority,
// this card was restating the same fact in older, vaguer words: two cards, one
// of them wrong. The trigger now carries what only IT knows — who you are
// signed in as — and says nothing about what you may do.
//
//   OPERATING AS   what you are acting as   tier · company · authority
//   THIS CARD      who you are              name · email
//
// The popover keeps the fuller picture, because that is where you go to check
// exactly which account and which company you are in before doing something
// you cannot undo.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "@/features/auth/AuthContext";
import { useRoleOverride } from "../roleOverride";
import { useActingContext, useSetActingTier } from "@/lib/context/useActingContext";

/* Mirror only. The authoritative store is roleOverride.ts (`lc_role_view_v1`);
   this key is kept written for v48-era components that still read it. */
const HYBRID_ROLE_KEY = "lc_hybrid_active_role_v1";

/**
 * The two perspectives a marketplace demo needs, expressed as TIERS.
 *
 * Not a Buyer/Supplier enum in the domain — a two-row lookup in one component
 * mapping a demonstrator-facing word onto the tier that already means it.
 * tier1 buys, mep supplies; that was true before demo mode existed and stays
 * true without it. Nothing outside this file reads it.
 */
const DEMO_PERSPECTIVES = [
  { tier: "tier1" as const, label: "Buying",    home: "/requirements", hint: "Acting as Tier-1 — post and award" },
  { tier: "mep"   as const, label: "Supplying", home: "/deals",        hint: "Acting as MEP — find and bid" },
];

export function ProfileMenu({ compact = false }: { compact?: boolean } = {}) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  /* GH #251 — this menu used to keep its OWN copy of the hybrid side:
     `lc_hybrid_active_role_v1` plus an `lc-hybrid-side-changed` event, entirely
     separate from the store the sidebar and mobile nav read
     (`roleOverride.ts` → `lc_role_view_v1` / `lc-role-override-changed`). Two
     independent stores for one concept, so flipping either left the other
     showing the opposite side.

     `useRoleOverride` is now the single source of truth. The legacy key and
     event are still WRITTEN, because v48-era components read them — dropping
     them would fix the sync and break those instead. */
  const [override, setOverride] = useRoleOverride();
  const { company, catalogue, tier, actable, demoMode } = useActingContext();
  const setTier = useSetActingTier();
  const hybridSide: "coordinator" | "partner" = override === "partner" ? "partner" : "coordinator";
  const flipHybridSide = (side: "coordinator" | "partner") => {
    setOverride(side);
    try { localStorage.setItem(HYBRID_ROLE_KEY, side); } catch { /* ignore */ }
    /* Kept for feature modules still subscribed to the old event. */
    try { window.dispatchEvent(new CustomEvent("lc-hybrid-side-changed", { detail: { side } })); } catch { /* ignore */ }
  };

  /* Outside-click + Escape close. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  /* v2: two grants at the active company, not a company `kind`. `override` is
     null whenever there is nothing to toggle, which is exactly the condition
     the old `user.role === "hybrid"` test was standing in for. */
  const isHybrid = override !== null;
  const authority = company?.role ?? null;
  const tierRow = catalogue.find((t) => t.code === tier) ?? null;

  return (
    <div className="relative" ref={ref}>
      {/* Avatar trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        /* v51 — the trigger sits INSIDE the dark rail, so its colours come
           from the shell tokens. Without this the name rendered near-black on
           navy and the row read as an empty gap. The popover below is
           deliberately left light: it floats over the content area, not the
           rail, so it belongs to the content's skin. */
        title={compact ? user.name : undefined}
        className={`w-full flex items-center gap-2.5 py-2 rounded-lc-md transition-colors ${
          compact ? "px-0 justify-center" : "px-2"
        }`}
        style={{ background: open ? "var(--lc-shell-2)" : undefined }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = "var(--lc-shell-2)"; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = ""; }}
      >
        {/* Flat avatar, not a gradient — brand-pack rule. Solid navy square
            with a white monogram. */}
        <div
          className="w-9 h-9 rounded-lc-sm text-white font-bold text-xs flex items-center justify-center shrink-0"
          style={{ background: "var(--color-lc-blue-dark)" }}
        >
          {user.initials}
        </div>
        {!compact && (
        <div className="min-w-0 flex-1 text-left">
          <div className="text-xs font-bold truncate" style={{ color: "var(--lc-shell-fg)" }}>
            {user.name}
          </div>
          {/* The email, not the role. Authority now lives in OPERATING AS as
              its own badge, and printing the v1 `user.role` here restated it in
              a word that no longer means one single thing. */}
          <div className="text-[10px] truncate" style={{ color: "var(--lc-shell-faint)" }}>
            {user.email}
          </div>
        </div>
        )}
        {!compact && <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-lc-faint)" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>}
      </button>

      {/* Floating menu */}
      {open && (
        <div
          role="menu"
          /* In a collapsed rail the trigger is 44px wide; a popover matching
             that would be unreadable, so it breaks out to a fixed width and
             anchors to the left edge instead of stretching. */
          className={`lc-v2 absolute bottom-full mb-2 bg-white rounded-lc-lg p-1 z-50 ${
            compact ? "left-0 w-60" : "left-0 right-0"
          }`}
          style={{ border: "1px solid var(--color-lc-line)",
                   boxShadow: "0 18px 40px -12px rgb(10 22 40 / .32)" }}
        >
          {/* v2 header. Names the account AND the tenant, because "am I in the
              right company" is the question worth answering before an action
              you cannot undo — and it is the one thing the collapsed trigger
              deliberately no longer says. */}
          <div className="px-3 pt-2.5 pb-2.5 mb-1 border-b"
               style={{ borderColor: "var(--color-lc-line)" }}>
            <div className="flex items-center gap-2.5">
              <span
                className="shrink-0 w-8 h-8 rounded-lc-sm inline-flex items-center justify-center text-[11px] font-extrabold text-white"
                style={{ background: "var(--color-lc-blue-dark)" }}
                aria-hidden="true"
              >
                {user.initials}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-bold truncate"
                      style={{ color: "var(--color-lc-ink)" }}>
                  {user.name}
                </span>
                <span className="block text-[11px] truncate"
                      style={{ color: "var(--color-lc-mute)" }}>
                  {user.email}
                </span>
              </span>
            </div>

            {(company || tierRow) && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {company && (
                  <span className="text-[11px] font-semibold truncate"
                        style={{ color: "var(--color-lc-ink-2)" }}>
                    {company.name}
                  </span>
                )}
                {authority && (
                  <span
                    className="text-[9.5px] font-extrabold tracking-wide px-1.5 py-0.5 rounded-lc-tag"
                    style={{ background: "var(--color-lc-bg-2)", color: "var(--color-lc-mute)" }}
                  >
                    {authority.toUpperCase()}
                  </span>
                )}
                {tierRow && (
                  <span
                    className="text-[9.5px] font-extrabold tracking-wide px-1.5 py-0.5 rounded-lc-tag"
                    style={{ background: "var(--color-lc-blue-light)", color: "var(--color-lc-blue-dark)" }}
                  >
                    TIER {tierRow.rank}
                  </span>
                )}
              </div>
            )}
          </div>

          <MenuItem
            label="View profile"
            onClick={() => { setOpen(false); navigate("/me"); }}
            icon={(
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-lc-blue-dark)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            )}
          />
          <MenuItem
            label="Account settings"
            onClick={() => { setOpen(false); navigate("/settings"); }}
            icon={(
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-lc-mute)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            )}
          />

          {/* ── DEMO MODE ──────────────────────────────────────────────
              Renders only when the SERVER says this deployment has demo mode
              on. The browser cannot turn it on — the flag arrives in
              /api/session/context, and a client that forges it gets a control
              whose every request the API then refuses.

              WHY THIS TOGGLE EXISTS ALONGSIDE THE RAIL'S VIEW-AS

              It is not a second way to do the thing. It is FEEDBACK. A
              demonstrator standing in front of a client needs to see, in the
              same place they see who they are signed in as, that this is a
              demo build and that the perspective just changed because THEY
              changed it — not because the product did something on its own.
              The rail's View-as switches silently and says nothing about
              being a demo.

              Both call the same setTier action on the same widened set, so
              there is one mechanism and two surfaces onto it — which is
              different from two mechanisms, and is why this does not repeat
              GH #251. Demo mode still changes only what is IN the set, never
              how the set is operated. */}
          {demoMode && (
            <>
              <div className="my-1 mx-2 border-t border-lc-line" />
              <div className="px-3 pt-2 pb-1.5 flex items-center gap-1.5">
                <span className="text-[10px] font-extrabold tracking-widest uppercase text-lc-faint">
                  Demo view
                </span>
                <span
                  className="text-[9px] font-extrabold tracking-wide px-1.5 py-0.5 rounded-lc-tag"
                  style={{ background: "var(--color-lc-amber-bg)", color: "var(--color-lc-amber-fg)" }}
                >
                  NOT PRODUCTION
                </span>
              </div>
              <p className="px-3 pb-1.5 text-[10.5px] leading-snug text-lc-faint">
                Switch perspective at {company?.name ?? "this company"}.
                Your authority does not change.
              </p>
              <div className="grid grid-cols-2 gap-1 px-1 pb-1">
                {DEMO_PERSPECTIVES.map(persp => {
                  const grant = actable.find(
                    a => a.tier_code === persp.tier
                      && a.company_id === (company?.company_id ?? null));
                  const here = tier === persp.tier;
                  return (
                    <button
                      key={persp.tier}
                      type="button"
                      disabled={!grant}
                      title={grant
                        ? `${persp.hint}${grant.via_demo ? " · via demo mode" : " · genuinely held"}`
                        : "Not available for this company"}
                      onClick={() => {
                        setTier(persp.tier, company?.company_id ?? null);
                        setOpen(false);
                        navigate(persp.home);
                      }}
                      className={`px-2 py-1.5 rounded-lc-sm text-[11px] font-bold transition-colors ${
                        here
                          ? "bg-lc-blue-light text-lc-blue-dark border border-lc-blue-border"
                          : "bg-white text-lc-mute border border-lc-line hover:border-lc-faint disabled:opacity-40"
                      }`}
                    >
                      {persp.label}
                      {/* Honest badge. A demonstrator must always be able to see
                          which tiers the company has actually bought — that is
                          frequently the thing being demonstrated. */}
                      {grant?.via_demo && (
                        <span className="ml-1 text-[8px] font-extrabold tracking-wide"
                              style={{ color: "var(--color-lc-amber)" }}>
                          DEMO
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div className="my-1 mx-2 border-t border-lc-line" />

          <MenuItem
            label="Sign out"
            danger
            onClick={() => { setOpen(false); signOut(); }}
            icon={(
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            )}
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label, onClick, danger, icon,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lc-md text-xs font-bold transition-colors text-left ${
        danger
          ? "text-lc-red-fg hover:bg-lc-red-bg"
          : "text-lc-ink-2 hover:bg-lc-bg-2"
      }`}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span>{label}</span>
    </button>
  );
}

